/**
 * This build's identifier — `ALPHA 202608121` — or `DEV` when there is not one.
 *
 * ── Where the string comes from ──────────────────────────────────────────────
 *
 * `scripts/build-number.sh` composes it at deploy time and `deploy.sh` passes it through the
 * Docker build as `GRIND_BUILD`, which the HUD stage turns into `VITE_GRIND_BUILD` so Vite bakes
 * it into the bundle. The client never computes any part of it: a date the *client* stamped would
 * be the player's clock rather than the build's, and two players on the same build would disagree
 * about which build they were on — which is the one thing a build number exists to prevent.
 *
 * ── Why a local build says DEV rather than inventing a number ────────────────
 *
 * `npm run dev` and a bare `docker build` both leave the variable empty, and a local build
 * genuinely has no ordinal — nothing counted it. Substituting today's date with an `N` of 1 would
 * make a developer's screenshot indistinguishable from a deployed build in a bug report, which is
 * precisely when the number is read. `DEV` is the honest answer and it is also the more useful
 * one.
 */

/** The phase-and-date string, or `DEV`. */
export function buildId(): string {
  // `import.meta.env` is replaced at build time, so this is a constant in the bundle rather than a
  // lookup. `??` and not `||` would be wrong here: an empty string is exactly what an unset build
  // arg produces, and it has to fall through.
  const raw = (import.meta.env.VITE_GRIND_BUILD as string | undefined) ?? '';
  return raw.trim() || 'DEV';
}

/** True when this bundle was cut by a deploy rather than built locally. */
export function isDeployedBuild(): boolean {
  return buildId() !== 'DEV';
}
